import * as Hogan from 'hogan.js';
import {Text, Sprite} from 'pixi.js';
import {
    consoleLogger,
    CommandLineInterface,
    CoreDI,
    Device,
    DeviceType,
    DisplayObjectUtils,
    IBrowser, inject,
    ObjectBuilder,
    Scroll
} from '@gongxigames/framework/core';
import {EnvironmentProperty, GameConfig} from '@gongxigames/framework/slot/types';
import {BottomPanelEvents} from '@gongxigames/framework/slot/modules/bottompanel/BottomPanelEvents';
import {InfoModule} from '@gongxigames/framework/slot/modules/info';
import {GameRulesEvents} from '@gongxigames/framework/slotV2/modules/gameRules';
import {
    AutoplayOptionsPopupComponent,
    AutoplayOptionsPopupComponentProps
} from '@gongxigames/framework/slot/modules/autoplayOptions/popup/AutoplayOptionsPopupComponent';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals';
import {HtmlSearch} from '@gongxigames/framework/core/fragments/HtmlSearch';

export class GameRules extends InfoModule {
    public static readonly NAME: symbol = Symbol('GameRules');

    @inject(CoreDI.Device)
    protected _device!: Device;

    @inject(CoreDI.DisplayObjectUtils)
    protected _displayObjectUtils!: DisplayObjectUtils;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    @inject(CoreDI.EnvironmentProperty)
    protected _environmentProperty!: EnvironmentProperty;

    @inject(CoreDI.CLI)
    protected _cli!: CommandLineInterface;

    @inject(CoreDI.Browser)
    protected _browser!: IBrowser;

    protected _gameRulesContainer!: HTMLElement;
    protected _gameRules!: HTMLElement;
    protected _scroll?: Scroll;

    protected _isOpen = false;
    protected _needToLoad = true;
    protected _loaded = false;

    protected htmlSearch!: HtmlSearch;

    public init(): void {
        super.init();

        this.createPage();

        /* develblock:start */
        this._cli.addCommand('gameRules', {
            show: () => {
                const menuBar = this._cli.getCommand('menuBar');

                // for mobile
                if (menuBar) {
                    if (typeof menuBar !== 'function') {
                        menuBar.showGameRules();
                    }
                } else {
                    this.onPageClicked();
                }
            },
            close: () => {
                this.close();

                const menuBar = this._cli.getCommand('menuBar');

                if (menuBar && typeof menuBar !== 'function') {
                    menuBar.close();
                }
            }
        });
        /* develblock:end */
    }

    protected async generateLocalizedImages(): Promise<void> {
        await this.generateTotalBetImage();
        await this.generateCashImage();
        await this.generateWinImage();
    }

    protected async generateTotalBetImage(): Promise<void> {
        const totalBetSprite = this._objectBuilder.buildSprite(ObjectBuilder.resources.shared_sprite.totalBet);
        const label = totalBetSprite.getChildByName<Text>('label');
        const fieldText = totalBetSprite.getChildByName<Text>('fieldText');

        label.text = this._languageService.get('totalBet').toUpperCase();
        fieldText.text = this._currencyFormatter.format(1, true);

        await this.generateTextureFromSprite('totalbet_img', totalBetSprite);
    }

    protected async generateCashImage(): Promise<void> {
        const cashSprite = this._objectBuilder.buildSprite(ObjectBuilder.resources.shared_sprite.cash);
        const fieldText = cashSprite.getChildByName<Text>('fieldText');

        if (this.isPlatform(DeviceType.DESKTOP)) {
            fieldText.text = `${this._languageService.get('cashLabel').toUpperCase()}: ${this._currencyFormatter.format(1000, true)}`;
        } else {
            const label = cashSprite.getChildByName<Text>('label');

            label.text = this._languageService.get('cashLabel').toUpperCase();
            fieldText.text = this._currencyFormatter.format(1000, true);
        }

        await this.generateTextureFromSprite('cash_img', cashSprite);
    }

    protected async generateWinImage(): Promise<void> {
        const winSprite = this._objectBuilder.buildSprite(ObjectBuilder.resources.shared_sprite.win);
        const moneyText = winSprite.getChildByName<Text>('fieldText');
        const winLabel = winSprite.getChildByName<Text>('label');

        moneyText.text = this._currencyFormatter.format(1000);
        winLabel.text = this._languageService.get('winLabel').toUpperCase();

        await this.generateTextureFromSprite('win_img', winSprite);
    }

    protected async generateTextureFromSprite(key: string, sprite: Sprite): Promise<void> {
        const texture = await this._displayObjectUtils.asyncGenerateTexture(sprite);

        if (this._loader.getTexture(key)) {
            consoleLogger.error(`Texture already exists: ${key}`);
        }

        this._loader.setTexture(key, texture);
    }

    public destroy(): void {
        super.destroy();

        if (this._gameRulesContainer && this._gameRulesContainer.parentElement) {
            this._gameRulesContainer.parentElement.removeChild(this._gameRulesContainer);
        }

        this._scroll?.removeListeners();
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.eventManager.on('General.Popups.Add', this.close.bind(this), this);
        strictUiEmitter.on('UI.menu.closed', this.close.bind(this));
        strictUiEmitter.on('UI.menu.gameRulesClicked', this.onPageClicked.bind(this));
        this.on(BottomPanelEvents.GAME_RULES_CLICKED, this.onPageClicked);
    }

    protected createPage(): void {
        this._gameRulesContainer = document.createElement('div');
        this._gameRulesContainer.id = 'gameRulesContainer';
        document.body.appendChild(this._gameRulesContainer);

        this._gameRules = document.createElement('div');
        this._gameRules.id = 'gameRules';
        this._gameRulesContainer.appendChild(this._gameRules);
    }

    protected onPageClicked(): void {
        if (this._isOpen) {
            return;
        }

        this._isOpen = true;

        if (this._needToLoad && !this._loaded) {
            this.load();
        } else if (this._loaded) {
            this.open();
        }
    }

    protected open(): void {
        super.open();

        this._currentContainer && this.eventManager.emit(GameRulesEvents.OPENED);
    }

    protected close(): void {
        if (!this._isOpen) {
            return;
        }

        super.close();

        this._isOpen = false;

        this._currentContainer && this.eventManager.emit(GameRulesEvents.CLOSED);
    }

    protected onCLose(): void {
        if (this.isPlatformDesktop()) {
            this.soundPlayer.play('buttonclick');
        }

        this.close();
    }

    protected onResized(): void {
        if (!this._isOpen) {
            return;
        }

        this._gameRulesContainer.classList.add('open');

        requestAnimationFrame(() => {
            this.fitToGameDimensions();

            if (this.isPlatformMobile()) {
                this.setContentSize();
            }

            if (this.isPlatformDesktop() && this._scroll) {
                // fix for CLFR-2477
                this._browser.addTimeout(() => this._scroll?.onResize());
            }
        });
    }

    protected load(): void {
        if (!this._needToLoad) {
            return;
        }

        const loader = this._resourceManager;
        const howToPlayText = this.isPlatformMobile() ? 'howToPlayMobile' : 'howToPlay';
        const templates = {
            gameRules: '',
            howToPlayMobile: '',
            howToPlay: ''
        };

        Promise.all([
            loader.loadHTMLTemplate('gameRules').then(template => {
                templates.gameRules = template;
            }),
            loader.loadTemplate(howToPlayText).then(template => {
                templates[howToPlayText] = template;
            }),
            this.createAutoplayOptionsPopupImageSource(),
            this.generateLocalizedImages()
        ]).then(() => this.loadCallback(templates.gameRules, templates[howToPlayText]));

        this._needToLoad = false;
    }

    protected async generateAutoplayOptionsImageSource(textureName: string, props: AutoplayOptionsPopupComponentProps): Promise<void> {
        const texture = await this._displayObjectUtils.asyncGenerateTextureFromReactComponent(AutoplayOptionsPopupComponent, props);

        if (this._loader.getTexture(textureName)) {
            consoleLogger.error(`Texture already exists: ${textureName}`);
        }

        this._displayObjectUtils.addTexturesToCache({[textureName]: texture});
        this._loader.setTexture(textureName, texture);
    }

    protected async createAutoplayOptionsPopupImageSource(): Promise<void> {
        const IMAGE_SOURCE_NAME = 'AutoplayPopup';
        const roundsOptions = this.settings.get('autoplayValues');
        const autoplayDefaultSelectedSpins = this.settings.get('autoplayDefaultSelectedSpins');
        const turboModeDisabled = this.settings.get('turboModeDisabled');
        const disabledLimits = this.settings.get('allAutoplayLimitsDisabled');
        const popupProperties: AutoplayOptionsPopupComponentProps = {
            disabledLimits,
            roundsOptions,
            autoplaySpins: roundsOptions[autoplayDefaultSelectedSpins],
            lossLimit: 1,
            turboModeDisabled
        };

        await this.generateAutoplayOptionsImageSource(IMAGE_SOURCE_NAME, popupProperties);
    }

    protected loadCallback(gameRulesTemplate: string, howToPlayTemplate: string): void {
        if (gameRulesTemplate && howToPlayTemplate) {
            this.insertHtml(gameRulesTemplate, this._gameRules);
            this.insertHtml(howToPlayTemplate, document.querySelector('#gameRules .howtoplay'));

            if (this._languageService.get('translationsGameTerminology') === 'undefined') {
                document.getElementById('translationsGameTerminology')!.style.display = 'none';
            }

            (document.querySelector('#gameRules .close') as any).onclick = this.onCLose.bind(this);

            this._loaded = true;
            this._isOpen && this.open();
            this.isPlatform(DeviceType.DESKTOP) && this.createCustomScroll();

            this.attachSearchField();
        } else {
            this._needToLoad = true;
            this._isOpen = false;
        }
    }

    protected attachSearchField() {
        if (this._settings.get('searchInfoFeatureEnabled')) {
            const close = this._gameRules.querySelector('.close');
            const topShadow = this._gameRules.querySelector('.top');

            this.htmlSearch = new HtmlSearch('#gameRules .content', 'current', this._languageService.get('search'));

            this._gameRules.insertBefore(this.htmlSearch.controls, close);
            topShadow?.classList.add('search-enabled');

            if (this._scroll) {
                this.htmlSearch.scroll = this._scroll;
            }

            this._gameRulesContainer.classList.add('search-component');
        }
    }

    protected insertHtml(templateString: any, container: any): void {
        const config = this._gameConfig;
        const RTPFormatted = this._currencyFormatter.formatNumber(config.RTP);
        const texts = {...this._languageService.texts};
        const settings = this.settings.getAllProperties() as any;
        const localSettingsOverrides = {...settings};
        const isShowWinProbability = this.settings.get('showWinProbability');
        const isShowMaxMultiplier = this.settings.get('showMaxMultiplier');
        const detailedRTP: Record<'feature' | 'rtp', string>[] = [];
        const isWaysGame = this.logic.isWaysGame();

        if (!isShowWinProbability) {
            delete texts.winProbability;
        }

        let valuesForTexts: Record<string, any> = {
            returnToPlayerDescription: {RTP: RTPFormatted},
            winProbability: isShowWinProbability ? {winProbability: config.winProbability} : undefined,
            maxMultiplier: isShowMaxMultiplier ? {maxMultiplier: config.maxMultiplier} : undefined,
            clientVersion: {clientVersion: config.clientVersion},
            minBet: {minBet: this._currencyFormatter.format(settings.minBet)},
            maxBet: {maxBet: this._currencyFormatter.format(settings.maxBet)}
        };

        if (this.settings.get('buyFeatureAvailable') && config.buyFeatureConfig?.featureNameTranslationKey) {
            const featureName = this._languageService.get(config.buyFeatureConfig?.featureNameTranslationKey);

            valuesForTexts = {
                ...valuesForTexts,
                buyFeatureDescription1: {featureName},
                buyFeatureDescription2: {
                    featureName,
                    featureMultiplier: this.logic.getBuyFeatureMultiplier()
                },
                buyFeatureDescription8: {
                    RTP: this._currencyFormatter.formatNumber(config.buyFeatureConfig?.RTP)
                }
            };
        }

        if (this.settings.get('showOddsUpfront')) {
            const {versions} = this._environmentProperty.buildProperty;
            // got from https://registry.rubyplay.tech/ui/repos/tree/General/maven-releases/rng-fortuna/rng-fortuna/1.0.1/rng-fortuna-1.0.1.jar
            const {RNGVersion = 'rng-fortuna-1.0.1.jar e0b1374f83ea1c643207ba0bbd80ed8463e3938f'} = settings;

            valuesForTexts = {...valuesForTexts,
                fileDate: {fileDate: new Date(versions.buildDate).toLocaleString()},
                serverVersion: {serverVersion: `${versions.math} ${this._gameConfig.mathHash}`},
                RNGVersion: {RNGVersion},
                betPredictionFormulaText: {wager: this.logic.getWager()},
                betPredictionFormulaTextWays: {wager: this.logic.getWager()}
            };
        } else {
            delete texts.fileDate;
            delete texts.serverVersion;
            delete texts.RNGVersion;
            delete texts.betPredictionFormulaText;
            delete texts.betPredictionFormulaTextWays;
        }

        if (settings.enableDetailedRTP) {
            for (const [key, val] of Object.entries(config.detailedRTP || {})) {
                const rtp = this._currencyFormatter.formatNumber(val);

                detailedRTP.push({
                    feature: this._languageService.get(key) !== 'undefined' ? this._languageService.get(key) : key,
                    rtp: `${rtp}%`
                });
            }
        }

        settings.showGameHistoryDescription = Boolean(this.settings.get('historyUrl'));
        localSettingsOverrides.buyFeatureAvailable = this.logic.shouldShowBuyFeatureDescription();

        for (const [textID, data] of Object.entries(valuesForTexts)) {
            if (this._languageService.get(textID) !== 'undefined' && data !== undefined) {
                texts[textID] = this._languageService.get(textID, data);
            }
        }

        const template = Hogan.compile(templateString);
        const lang = this.settings.get('lang');
        const images = this._templateUtils.createImageMap(templateString, lang);

        const html = template.render({
            images,
            texts,
            detailedRTP,
            isWaysGame,
            settings,
            ...localSettingsOverrides
        });

        container.insertAdjacentHTML('beforeend', html);
    }

    protected createCustomScroll(): void {
        this._scroll = new Scroll(this._gameRules, this._device, {searchEnabled: this.settings.get('searchInfoFeatureEnabled')});
    }

    public setCurrentElements(): void {
        this._currentContainer = this._gameRulesContainer;
        this._currentPage = this._gameRules;
        this._currentScroll = this._scroll!;
    }
}
